import React from 'react';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@/components/buttons/Button';
import Form from '@/components/form/Form';
import {
  useHomeHeroAuthenticationStepActions,
  useHomeHeroAuthenticationStepData,
} from '@/store/authentication/AuthenticationStore';
import TextField from '@/components/form/text/TextField';
import { loginSuccess } from '@/store/actions/auth';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { useDispatch, useSelector } from 'react-redux';
import PromptMessage from '@/components/UI/PromptMessage';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { triggerNetreferSignup } from '@/shared/netrefer';
import { selectRegistrationOpenedFrom } from '@/store/ui/selectors';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { AuthMethod, BffAuthResponse } from '@lucky7ventures/bff-types';
import BffApiService from '@/shared/bffApiService';
import { BffApiError } from '@/models/bff-api-error';
import { sendEventToAppWrapper } from '@/utils/app-wrapper-utils';

const initialValues = {
  otp: '',
};

const TwoFactorAuthenticationForm = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { loading, error, request } = useBffApiRequest<BffAuthResponse>();
  const locale = useSelector(selectLocale);
  const registrationOpenedFrom = useSelector(selectRegistrationOpenedFrom);
  const navigate = useAbsoluteNavigate();
  const { request: setAuthenticationMethodRequest } = useBffApiRequest<null>();

  const validationSchema = Yup.object().shape({
    otp: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
  });

  const { username, password, postRegisterData } = useHomeHeroAuthenticationStepData();
  const { reset } = useHomeHeroAuthenticationStepActions();

  const successCallback = (data: BffAuthResponse) => {
    // case when the 2fa flow is done after the registration

    if (postRegisterData) {
      triggerGTag(GTagEvents.fast_reg_success, { userId: postRegisterData.userId });
      sendEventToAppWrapper(GTagEvents.fast_reg_success, {
        userId: postRegisterData.userId,
      });
      triggerNetreferSignup(postRegisterData.btag, postRegisterData.userId, locale);
      dispatch(loginSuccess(data.token, { isRegistration: true, blockReason: data.blockReason }));

      // this request will disable the 2fa for the user post-registration
      setAuthenticationMethodRequest({
        apiMethod: BffApiService.setAuthenticationMethod,
        pathParams: {
          authMethod: AuthMethod.OneFactor,
        },
      });

      if (registrationOpenedFrom === OpenedFrom.RewardsWheel) {
        navigate('/rewards-wheel');
      }
    } else {
      // case when the 2fa flow is done after the login
      triggerGTag(GTagEvents.login_2fa_success);
      dispatch(loginSuccess(data.token, { blockReason: data.blockReason }));
    }

    // reset the Auth store
    reset();
  };

  const errorCallback = (error: BffApiError) => {
    triggerGTag(GTagEvents.login_2fa_error, {
      errorId: error.code,
      error: `bff-error-${error.code}}`,
    });
  };

  const handleResetCode = () => {
    const payload = { username: username ?? '', password: password ?? '' };

    request({
      apiMethod: BffApiService.login,
      payload,
      errorCallback: errorCallback,
      successCallback: () => {
        triggerGTag(GTagEvents.login_2fa);
      },
    });
  };

  const handleSubmit = ({ otp }: { otp: string }) => {
    // case when the user comes to the 2fa form after the registration
    triggerGTag(GTagEvents.login_2fa_submit);
    if (username && password) {
      request({
        apiMethod: BffApiService.loginOtp,
        payload: { otp, username, password },
        successCallback,
        errorCallback,
      });
    }
  };

  return (
    <Form
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      className="flex flex-col w-full justify-between gap-7"
    >
      {() => (
        <>
          <TextField
            data-cy="two-factor-authentication-form-code"
            label={intl.formatMessage({ id: 'inputs.code' })}
            name="otp"
            className="text-white border-[1px] border-transparent focus-within:border-blue-light bg-white/20"
          />
          <div className="flex flex-col items-center mt-4">
            {error && (
              <PromptMessage
                data-cy="2fa-error"
                type="error"
                message={
                  error.code === 9238
                    ? `${intl.formatMessage({ id: 'error.sendingSmsFailed' })}`
                    : `${intl.formatMessage({ id: 'error.support' })} [${error.code}]`
                }
              />
            )}
            <Button
              data-cy="two-factor-authentication-form-submit-button"
              text={intl.formatMessage({ id: 'authentication.two-factor-authentication.verify' })}
              type="submit"
              btnType="primary"
              isLoading={loading}
            />
            <div className="text-white text-[15px] text-center mt-6">
              <FormattedMessage
                id="authentication.two-factor-authentication.description"
                values={{
                  a: str => (
                    <span className="text-blue-light font-bold underline cursor-pointer">
                      {str}
                    </span>
                  ),
                }}
              />
            </div>
            <p className="max-w-[80%] mt-3 block !text-[12px] italic text-white mb-0">
              <FormattedMessage
                id="authentication.two-factor-authentication.change-to-login"
                values={{
                  a: str => (
                    <span
                      data-cy="two-factor-authentication-form-switch-login-button"
                      className="text-blue-light font-bold underline cursor-pointer"
                      onClick={handleResetCode}
                    >
                      {str}
                    </span>
                  ),
                }}
              />
            </p>
          </div>
        </>
      )}
    </Form>
  );
};

export default TwoFactorAuthenticationForm;
